import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, X, Smartphone, Wifi, WifiOff } from 'lucide-react'
import { usePWA } from '../hooks/usePWA'
import { Button } from './ui/button'

export default function PWAPrompt() {
  const { isOnline, canInstall, installApp } = usePWA()
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Show install prompt if can install and not dismissed
    if (canInstall && !localStorage.getItem('pwa-install-dismissed')) {
      setTimeout(() => setShowInstallPrompt(true), 3000)
    }
  }, [canInstall])

  const handleInstall = async () => {
    await installApp()
    setShowInstallPrompt(false)
  }

  const handleDismissInstall = () => {
    setShowInstallPrompt(false)
    setDismissed(true)
    localStorage.setItem('pwa-install-dismissed', 'true')
  }

  return (
    <>
      {/* Online/Offline Status */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="fixed top-0 left-0 right-0 bg-orange-500 text-white px-4 py-2 text-center text-sm font-medium z-50"
          >
            <div className="flex items-center justify-center space-x-2">
              <WifiOff className="w-4 h-4" />
              <span>Modalità offline - I tuoi dati sono salvati localmente</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Install Prompt */}
      <AnimatePresence>
        {showInstallPrompt && !dismissed && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-4 right-4 max-w-md mx-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl p-4 shadow-2xl z-40"
          >
            <div className="flex items-start space-x-3">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="p-2 bg-white/20 rounded-full"
              >
                <Smartphone className="w-6 h-6" />
              </motion.div>
              
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">📱 Installa Gitaigo</h3>
                <p className="text-white/90 text-sm mb-3">
                  Aggiungi alla home screen per un accesso rapido e un'esperienza app nativa!
                </p>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleInstall}
                    className="bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Installa
                  </Button>
                  <Button
                    onClick={handleDismissInstall}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                    size="sm"
                  >
                    Più tardi
                  </Button>
                </div>
              </div>
              
              <button
                onClick={handleDismissInstall}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Status Indicator */}
      <div className="fixed bottom-4 right-4 z-30">
        <motion.div
          className={`p-2 rounded-full shadow-lg ${
            isOnline 
              ? 'bg-green-500 text-white' 
              : 'bg-orange-500 text-white'
          }`}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {isOnline ? (
            <Wifi className="w-4 h-4" />
          ) : (
            <WifiOff className="w-4 h-4" />
          )}
        </motion.div>
      </div>
    </>
  )
}